import { NEW_BASE_URL } from "../config";
import { generalHeader } from "../cycles/headers";
import { v4 as uuidv4 } from "uuid";

/**
 *  - Singleton service for managing FAQs
 *
 * This service provides a centralized interface for fetching and managing
 * FAQs from the asset management content API. It handles FAQ list,
 * search, and caching functionality.
 */
class FAQService {
  constructor() {
    // Configuration
    this.baseUrl = `${NEW_BASE_URL}/FaqProxy`;
    this.application = null;
    this.userId = null;

    // State management
    this.faqList = null;
    this.searchResults = null;
    this.faqCache = new Map();
    this.searchCache = new Map();
  }

  /**
   * Set the application ID for all requests
   * @param {string} applicationId - The application ID to use in headers
   */
  setApplication(applicationId) {
    this.application = applicationId;
  }

  /**
   * Set the user ID for all requests
   * @param {string} userId - The user ID to use in headers
   */
  setUserId(userId) {
    this.userId = userId;
  }

  /**
   * Generate a unique request ID
   * @returns {string} UUID v4
   */
  generateRequestId() {
    return uuidv4();
  }

  /**
   * Creates a request header for the content API
   * @private
   */
  createRequestHeader(userId) {
    // Use configured values or fallback to defaults
    const effectiveUserId =
      userId || this.userId || "123e4567-e89b-12d3-a456-426614174000";
    const effectiveApplication =
      this.application || "03FC0B90-DFAD-11EE-8D86-0800200C9A66";

    return {
      id: this.generateRequestId(),
      application: effectiveApplication,
      bank: "NBG",
      userId: effectiveUserId,
    };
  }

  /**
   * Build standard request body with header and payload
   * @param {Object} payload - The payload data
   * @param {string} userId - Optional user ID override
   * @returns {Object} Complete request body
   */
  buildRequestBody(payload = {}, userId = null) {
    return {
      header: this.createRequestHeader(userId),
      payload,
    };
  }

  /**
   * Get FAQ list using POST method with standard request structure
   * @param {string} userId - Optional user ID override
   * @param {string} csrfToken - CSRF token for authentication
   * @returns {Promise<Array>} List of FAQs
   */
  async getFAQList(userId = null, csrfToken) {
    try {
      const requestBody = {
        header: {
          userId: userId || "00000000-0000-0000-0000-000000000000",
          bank: "NBG",
        },
        payload: {},
      };
      const response = await fetch(`${this.baseUrl}/list`, {
        method: "POST",
        headers: generalHeader(csrfToken, true),
        credentials: "include",
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Check for API-level exceptions
      if (data.exception) {
        throw new Error(
          `API Exception (${data.exception.code}): ${data.exception.description}`
        );
      }

      if (data.payload && data.payload.faqs) {
        this.faqList = data.payload.faqs;
        return data.payload.faqs;
      }

      return [];
    } catch (error) {
      console.error("Error fetching FAQ list:", error);
      throw error;
    }
  }

  /**
   * Get FAQ list using GET method with query parameters
   * @param {Object} params - Query parameters (keyword, category, sticky, promote)
   * @returns {Promise<Array>} List of FAQs
   */
  async getFAQListByParams(params = {}) {
    try {
      let url = `${this.baseUrl}/faq/list`;

      // Add query parameters if provided
      const queryParams = new URLSearchParams();
      if (params.keyword) queryParams.append("keyword", params.keyword);
      if (params.category) queryParams.append("category", params.category);
      if (params.sticky) queryParams.append("sticky", "true");
      if (params.promote) queryParams.append("promote", "true");

      if (queryParams.toString()) {
        url += `?${queryParams.toString()}`;
      }

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.faqs) {
        this.faqList = data.faqs;
        return data.faqs;
      }

      return [];
    } catch (error) {
      console.error("Error fetching FAQs by params:", error);
      throw error;
    }
  }

  /**
   * Fetches all FAQs from the API using the search endpoint (backward compatibility)
   * @param {Object} params - Parameters for filtering
   * @param {string} params.keyword - Search keyword
   * @param {string} params.category - Category filter
   * @param {boolean} params.sticky - Get only sticky FAQs
   * @param {boolean} params.promote - Get only promoted FAQs
   * @param {string} userId - User ID for authentication
   * @param {string} csrfToken - CSRF token for authentication
   * @returns {Promise<Object>} FAQ list response
   */
  async getFaqList(params = {}, userId, csrfToken) {
    // Use search endpoint with empty payload to get all FAQs
    return this.searchFaqs("", "", userId, csrfToken);
  }

  /**
   * Search FAQs with keyword or category (POST method)
   * @param {Object} searchParams - Search parameters
   * @param {string} searchParams.keyword - Search keyword
   * @param {string} searchParams.category - Filter by category
   * @param {string} userId - Optional user ID override
   * @param {string} csrfToken - CSRF token for authentication
   * @returns {Promise<Array>} Filtered FAQ list
   */
  async searchFAQs(searchParams = {}, userId = null, csrfToken) {
    try {
      const requestBody = {
        header: {
          userId: userId || "00000000-0000-0000-0000-000000000000",
          bank: "NBG",
        },
        payload: searchParams,
      };
      const response = await fetch(`${this.baseUrl}/search`, {
        method: "POST",
        headers: generalHeader(csrfToken, true),
        credentials: "include",
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Check for API-level exceptions
      if (data.exception) {
        throw new Error(
          `API Exception (${data.exception.code}): ${data.exception.description}`
        );
      }

      if (data.payload && data.payload.faqs) {
        this.searchResults = data.payload.faqs;
        return data.payload.faqs;
      }

      return [];
    } catch (error) {
      console.error("FAQService search error:", error);
      throw error;
    }
  }

  /**
   * Searches FAQs with keyword and/or category (backward compatibility)
   * @param {string} keyword - Search keyword
   * @param {string} category - Category filter
   * @param {string} userId - User ID for authentication
   * @param {string} csrfToken - CSRF token for authentication
   * @returns {Promise<Object>} Search results
   */
  async searchFaqs(keyword = "", category = "", userId, csrfToken) {
    const cacheKey = `${keyword}_${category}`;

    // Check cache first
    if (this.searchCache.has(cacheKey)) {
      return this.searchCache.get(cacheKey);
    }

    try {
      const payload = {};

      if (keyword) {
        payload.keyword = keyword;
      }

      if (category) {
        payload.category = category;
      }

      const requestBody = {
        header: {
          userId: userId || "00000000-0000-0000-0000-000000000000",
          bank: "NBG",
        },
        payload: payload,
      };

      const response = await fetch(`${this.baseUrl}/search`, {
        method: "POST",
        headers: generalHeader(csrfToken, true),
        credentials: "include",
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Handle API response structure
      if (data.exception) {
        console.error("API Exception:", data.exception);
        throw new Error(data.exception.description);
      }

      // Cache the result
      if (data.payload) {
        this.searchCache.set(cacheKey, data);
      }

      return data;
    } catch (error) {
      console.error("Error searching FAQs:", error);
      throw error;
    }
  }

  /**
   * Refresh the FAQ cache
   * @param {string} userId - Optional user ID override
   * @param {string} csrfToken - CSRF token for authentication
   * @returns {Promise<Object>} Refresh status
   */
  async refreshFAQs(userId = null, csrfToken) {
    try {
      const requestBody = {
        header: {
          userId: userId || "00000000-0000-0000-0000-000000000000",
          bank: "NBG",
        },
        payload: {},
      };
      const response = await fetch(`${this.baseUrl}/refresh`, {
        method: "POST",
        headers: generalHeader(csrfToken, true),
        credentials: "include",
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Clear local caches
      this.faqCache.clear();
      this.searchCache.clear();
      this.faqList = null;
      this.searchResults = null;

      return data.payload || { status: "completed" };
    } catch (error) {
      console.error("Error refreshing FAQs:", error);
      throw error;
    }
  }

  /**
   * Refreshes the FAQ cache (backward compatibility)
   * @param {string} userId - User ID for authentication
   * @param {string} csrfToken - CSRF token for authentication
   * @returns {Promise<Object>} Refresh status
   */
  async refreshFaqCache(userId, csrfToken) {
    return this.refreshFAQs(userId, csrfToken);
  }

  /**
   * Get the currently loaded FAQ list
   * @returns {Array|null} Current FAQ list or null
   */
  getCachedFAQList() {
    return this.faqList;
  }

  /**
   * Get the search results
   * @returns {Array|null} Search results or null
   */
  getSearchResults() {
    return this.searchResults;
  }

  /**
   * Gets the current FAQ list from memory (backward compatibility)
   * @returns {Object|null} Current FAQ list or null
   */
  getCurrentFaqList() {
    return this.faqList;
  }

  /**
   * Extracts categories from FAQ list
   * @param {Array} faqs - Array of FAQs
   * @returns {Array} List of unique categories
   */
  extractCategoriesFromFaqs(faqs) {
    const categoryMap = new Map();

    faqs.forEach((faq) => {
      const categories = faq.faqCategories || [];
      categories.forEach((cat) => {
        if (!categoryMap.has(cat)) {
          categoryMap.set(cat, {
            name: cat,
            id: cat,
          });
        }
      });
    });

    return Array.from(categoryMap.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }

  /**
   * Gets cached search results
   * @param {string} keyword - Search keyword
   * @param {string} category - Category
   * @returns {Object|null} Cached results or null
   */
  getCachedSearch(keyword, category) {
    const cacheKey = `${keyword}_${category}`;
    return this.searchCache.get(cacheKey) || null;
  }

  /**
   * Download FAQs as JSON file
   * @param {Array} faqs - FAQ data to download
   * @param {string} filename - Optional filename
   */
  downloadFAQs(faqs = null, filename = null) {
    const faqsToDownload = faqs || this.faqList;

    if (!faqsToDownload) {
      console.error("No FAQs to download");
      return false;
    }

    const defaultFilename = filename || `faqs_${Date.now()}.json`;

    try {
      const dataStr = JSON.stringify(faqsToDownload, null, 2);
      const dataBlob = new Blob([dataStr], { type: "application/json" });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = defaultFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      return true;
    } catch (error) {
      console.error("Error downloading FAQs:", error);
      return false;
    }
  }

  /**
   * Get unique categories from FAQs
   * @param {Array} faqs - FAQ list to extract categories from
   * @returns {Array} Unique categories
   */
  getCategories(faqs = null) {
    const faqsToProcess = faqs || this.faqList;

    if (!faqsToProcess || !Array.isArray(faqsToProcess)) {
      return [];
    }

    const categories = new Set();

    faqsToProcess.forEach((faq) => {
      if (faq.faqCategories && Array.isArray(faq.faqCategories)) {
        faq.faqCategories.forEach((category) => categories.add(category));
      }
    });

    return Array.from(categories).sort();
  }

  /**
   * Filter FAQs by category
   * @param {string} category - Category to filter by
   * @param {Array} faqs - FAQ list to filter (uses cached list if not provided)
   * @returns {Array} Filtered FAQs
   */
  filterByCategory(category, faqs = null) {
    const faqsToFilter = faqs || this.faqList;

    if (!faqsToFilter || !Array.isArray(faqsToFilter)) {
      return [];
    }

    return faqsToFilter.filter(
      (faq) =>
        faq.faqCategories &&
        Array.isArray(faq.faqCategories) &&
        faq.faqCategories.includes(category)
    );
  }

  /**
   * Get FAQ statistics
   * @param {Array} faqs - FAQ list to analyze
   * @returns {Object} Statistics object
   */
  getStatistics(faqs = null) {
    const faqsToAnalyze = faqs || this.faqList;

    if (!faqsToAnalyze || !Array.isArray(faqsToAnalyze)) {
      return {
        total: 0,
        sticky: 0,
        promoted: 0,
        categories: [],
      };
    }

    return {
      total: faqsToAnalyze.length,
      sticky: faqsToAnalyze.filter((f) => f.sticky).length,
      promoted: faqsToAnalyze.filter((f) => f.promote).length,
      categories: this.getCategories(faqsToAnalyze),
    };
  }

  /**
   * Clears all cached data
   */
  clear() {
    this.faqList = null;
    this.searchResults = null;
    this.faqCache.clear();
    this.searchCache.clear();
  }
}

export default new FAQService();
